import { useTranslation } from '@/src/i18n';
import { Ionicons } from '@expo/vector-icons';
import { Image, Pressable, ScrollView, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { createStyles } from './UseCaseLandingScreen.styles';

export interface UseCaseStep {
  title: string;
  titleEs: string;
  desc: string;
  descEs: string;
}

export interface UseCaseFeature {
  icon: string;
  color: string;
  title: string;
  titleEs: string;
  desc: string;
  descEs: string;
}

export interface UseCaseLandingConfig {
  badge: string;
  badgeEs: string;
  badgeIcon: string;
  title: string;
  titleEs: string;
  titleAccent: string;
  titleAccentEs: string;
  subtitle: string;
  subtitleEs: string;
  isPro?: boolean;
  steps: UseCaseStep[];
  features: UseCaseFeature[];
  relatedBlogSlug?: string;
  relatedBlogTitle?: string;
  relatedBlogTitleEs?: string;
  relatedBlogSummary?: string;
  relatedBlogSummaryEs?: string;
}

export interface UseCaseLandingScreenProps {
  config: UseCaseLandingConfig;
  isDark: boolean;
  onToggleTheme: () => void;
  onGetStarted: () => void;
  onSignIn: () => void;
  onBlogPostPress?: (slug: string) => void;
  colors: {
    background: string;
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
    surface: string;
  };
}

export const UseCaseLandingScreen = ({
  config,
  isDark,
  onToggleTheme,
  onGetStarted,
  onSignIn,
  onBlogPostPress,
  colors,
}: UseCaseLandingScreenProps) => {
  const { t, language } = useTranslation();
  const { width } = useWindowDimensions();
  const isWide = width >= 768;
  const styles = createStyles(isWide);

  const isEs = language === 'es';

  const badge = isEs ? config.badgeEs : config.badge;
  const title = isEs ? config.titleEs : config.title;
  const titleAccent = isEs ? config.titleAccentEs : config.titleAccent;
  const subtitle = isEs ? config.subtitleEs : config.subtitle;
  const relatedTitle = isEs ? config.relatedBlogTitleEs : config.relatedBlogTitle;
  const relatedSummary = isEs ? config.relatedBlogSummaryEs : config.relatedBlogSummary;

  return (
    <View style={{ flex: 1 }}>
      <ScrollView style={[styles.container, { backgroundColor: colors.background }]}>

        {/* ── Header ── */}
        <View style={[styles.header, { backgroundColor: colors.background, borderBottomColor: colors.border }]}>
          <Pressable
            style={styles.headerLogoContainer}
            onPress={onSignIn}
          >
            <Image
              source={require('@/assets/images/android-icon-foreground.png')}
              style={styles.headerLogoIcon}
            />
            <Text style={[styles.headerLogo, { color: colors.primary }]}>Tournaly</Text>
          </Pressable>
          <View style={styles.headerActions}>
            <TouchableOpacity style={styles.headerThemeToggle} onPress={onToggleTheme}>
              <Ionicons name={isDark ? 'sunny' : 'moon'} size={20} color={colors.text} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.headerSignIn, { borderColor: colors.primary }]}
              onPress={onSignIn}
            >
              <Text style={[styles.headerSignInText, { color: colors.primary }]}>
                {t('landing.signIn')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* ── Hero ── */}
        <View style={styles.heroSection}>
          <View style={[styles.heroBadge, { borderColor: colors.primary + '40', backgroundColor: colors.primary + '12' }]}>
            <Ionicons name={config.badgeIcon as any} size={14} color={colors.primary} />
            <Text style={[styles.heroBadgeText, { color: colors.primary }]}>{badge}</Text>
          </View>

          <Text style={[styles.heroTitle, { color: colors.text }]}>
            {title + '\n'}
            <Text style={[styles.heroTitleAccent, { color: colors.primary }]}>{titleAccent}</Text>
          </Text>

          <Text style={[styles.heroSubtitle, { color: colors.textSecondary }]}>{subtitle}</Text>

          {config.isPro && (
            <Text style={[styles.heroProNote, { color: colors.primary, backgroundColor: colors.primary + '18' }]}>
              {isEs ? '✦ Incluido en Tournaly Pro' : '✦ Included in Tournaly Pro'}
            </Text>
          )}

          <View style={styles.heroButtons}>
            <TouchableOpacity
              style={[styles.heroPrimaryBtn, { backgroundColor: colors.primary }]}
              onPress={onGetStarted}
            >
              <Text style={[styles.heroPrimaryBtnText, { color: colors.primaryText }]}>
                {t('landing.getStartedFree')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.heroSecondaryBtn, { borderColor: colors.border }]}
              onPress={onSignIn}
            >
              <Text style={[styles.heroSecondaryBtnText, { color: colors.text }]}>
                {t('landing.signIn')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* ── How it works ── */}
        <View style={[styles.sectionAlt, { backgroundColor: colors.surface }]}>
          <Text style={[styles.sectionLabel, { color: colors.primary }]}>
            {isEs ? 'CÓMO FUNCIONA' : 'HOW IT WORKS'}
          </Text>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            {isEs ? 'En 3 pasos' : '3 steps to your tournament'}
          </Text>
          <View style={styles.stepsRow}>
            {config.steps.map((step, i) => (
              <View key={i} style={styles.stepItem}>
                {!isWide && i > 0 && (
                  <View style={[styles.stepConnector, { backgroundColor: colors.border }]} />
                )}
                <View style={[styles.stepNumber, { backgroundColor: colors.primary }]}>
                  <Text style={[styles.stepNumberText, { color: colors.primaryText }]}>{i + 1}</Text>
                </View>
                <Text style={[styles.stepTitle, { color: colors.text }]}>
                  {isEs ? step.titleEs : step.title}
                </Text>
                <Text style={[styles.stepDesc, { color: colors.textSecondary }]}>
                  {isEs ? step.descEs : step.desc}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* ── Key features ── */}
        <View style={styles.section}>
          <Text style={[styles.sectionLabel, { color: colors.primary }]}>
            {isEs ? 'CARACTERÍSTICAS' : 'FEATURES'}
          </Text>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            {isEs ? 'Todo lo que necesitas' : 'Everything you need'}
          </Text>
          <View style={styles.featuresGrid}>
            {config.features.map((feature, i) => (
              <View key={i} style={[styles.featureCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <View style={[styles.featureIconBox, { backgroundColor: feature.color + '20' }]}>
                  <Ionicons name={feature.icon as any} size={20} color={feature.color} />
                </View>
                <View style={styles.featureText}>
                  <Text style={[styles.featureTitle, { color: colors.text }]}>
                    {isEs ? feature.titleEs : feature.title}
                  </Text>
                  <Text style={[styles.featureDesc, { color: colors.textSecondary }]}>
                    {isEs ? feature.descEs : feature.desc}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* ── Related blog post ── */}
        {config.relatedBlogSlug && relatedTitle && (
          <View style={[styles.sectionAlt, { backgroundColor: colors.surface }]}>
            <Text style={[styles.sectionLabel, { color: colors.primary }]}>
              {isEs ? 'APRENDE MÁS' : 'LEARN MORE'}
            </Text>
            <View style={[styles.relatedCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <View style={[styles.relatedIcon, { backgroundColor: colors.primary + '18' }]}>
                <Ionicons name="book-outline" size={24} color={colors.primary} />
              </View>
              <View style={styles.relatedContent}>
                <Text style={[styles.relatedLabel, { color: colors.primary }]}>
                  {isEs ? 'GUÍA COMPLETA' : 'COMPLETE GUIDE'}
                </Text>
                <Text style={[styles.relatedTitle, { color: colors.text }]}>{relatedTitle}</Text>
                {relatedSummary && (
                  <Text style={[styles.relatedSummary, { color: colors.textSecondary }]}>{relatedSummary}</Text>
                )}
                <TouchableOpacity
                  style={styles.relatedLink}
                  onPress={() => onBlogPostPress?.(config.relatedBlogSlug!)}
                >
                  <Text style={[styles.relatedLinkText, { color: colors.primary }]}>
                    {isEs ? 'Leer guía' : 'Read guide'}
                  </Text>
                  <Ionicons name="arrow-forward" size={14} color={colors.primary} />
                </TouchableOpacity>
              </View>
            </View>
          </View>
        )}

        {/* ── Bottom CTA ── */}
        <View style={[styles.ctaSection, { backgroundColor: colors.primary + '10' }]}>
          <Text style={[styles.ctaTitle, { color: colors.text }]}>
            {isEs ? 'Empieza gratis hoy' : 'Start for free today'}
          </Text>
          <Text style={[styles.ctaSubtitle, { color: colors.textSecondary }]}>
            {isEs
              ? 'Sin tarjeta de crédito. Sin instalación. Tu primer torneo en minutos.'
              : 'No credit card. No installation. Your first tournament in minutes.'}
          </Text>
          <TouchableOpacity
            style={[styles.ctaBtn, { backgroundColor: colors.primary }]}
            onPress={onGetStarted}
          >
            <Text style={[styles.ctaBtnText, { color: colors.primaryText }]}>
              {t('landing.getStartedFree')}
            </Text>
          </TouchableOpacity>
        </View>

        {/* ── Footer ── */}
        <View style={[styles.footer, { borderTopColor: colors.border }]}>
          <Text style={[styles.footerCopy, { color: colors.textTertiary }]}>
            © {new Date().getFullYear()} Tournaly
          </Text>
          <View style={styles.footerLinks}>
            <TouchableOpacity onPress={() => onBlogPostPress?.('blog')}>
              <Text style={[styles.footerLink, { color: colors.textSecondary }]}>Blog</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => onBlogPostPress?.('privacy')}>
              <Text style={[styles.footerLink, { color: colors.textSecondary }]}>
                {isEs ? 'Privacidad' : 'Privacy'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => onBlogPostPress?.('terms')}>
              <Text style={[styles.footerLink, { color: colors.textSecondary }]}>
                {isEs ? 'Términos' : 'Terms'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>

      </ScrollView>
    </View>
  );
};
